import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeProvider } from "next-themes";
import { IntlProvider } from "react-intl";
import { useState, lazy, Suspense } from "react";
import { LOCALES, getSavedLocale, saveLocale, SupportedLocale } from "./i18n";
import NotFound from "./pages/NotFound";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useAuth } from "./hooks/useAuth";
import { usePapel, alcanca, homeDoPapel, type Papel } from "./hooks/usePapel";
import { ALUNO_ENTRA_PELA_SALA } from "./branding/identidade";
import { Casca } from "./components/nav/Casca";
import { PortaDaMatricula } from "./components/PortaDaMatricula";
import { TEM_MENU_LATERAL } from "./lib/navegacao";
import { FIXED_BRAND_ID } from "./branding/product";
import { BrandingProvider } from "./branding";

const Admin = lazy(() => import("./pages/Admin"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Logs = lazy(() => import("./pages/Logs"));
const EditVideo = lazy(() => import("./pages/EditVideo"));
const Student = lazy(() => import("./pages/Student"));
const CourseCatalog = lazy(() => import("./pages/CourseCatalog"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const DebugPanel = lazy(() => import("./components/DebugPanel"));
const AuthPage = lazy(() => import("./components/auth/AuthPage"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const AdminTurmas = lazy(() => import("./pages/AdminTurmas"));
const AdminAlunos = lazy(() => import("./pages/AdminAlunos"));
const CriarConta = lazy(() => import("./pages/CriarConta"));
const AdminEscola = lazy(() => import("./pages/AdminEscola"));
const ClassConsole = lazy(() => import("./pages/ClassConsole"));
const ClassSessionConsole = lazy(() => import("./pages/ClassSessionConsole"));
const Sala = lazy(() => import("./pages/Sala"));
const NovaSenha = lazy(() => import('./pages/NovaSenha'));
const TrocarSenha = lazy(() => import('./pages/TrocarSenha'));
const Perfil = lazy(() => import('./pages/Perfil'));
const PainelEscola = lazy(() => import('./pages/PainelEscola'));
const MinhasAulas = lazy(() => import('./pages/MinhasAulas'));
const ReassistirAula = lazy(() => import('./pages/ReassistirAula'));
const EntrarNaSala = lazy(() => import('./pages/EntrarNaSala'));
const Telao = lazy(() => import("./pages/Telao"));
const ClassResultados = lazy(() => import("./pages/ClassResultados"));
const ClassRegistro = lazy(() => import("./pages/ClassRegistro"));
const Painel = lazy(() => import("./pages/Painel"));

const queryClient = new QueryClient();

/**
 * Rota protegida, agora com papel mínimo.
 *
 * Antes só perguntava "está logado?", e as 14 rotas usavam a mesma porta — um
 * aluno logado que digitasse /admin/turmas ABRIA a tela de provisionar contas
 * da escola. A RLS e as Edge Functions impediam a ação, mas a tela aparecia.
 *
 * `minimo` é o papel MENOR que ainda entra. Sem `minimo`, a rota exige apenas
 * sessão — é o comportamento antigo, mantido para as telas do aluno.
 *
 * Isto é uma tranca de UI, NÃO autorização. Quem autoriza continua sendo a RLS
 * e o servidor; o bundle é público e qualquer pessoa lê estas regras.
 */
function ProtectedRoute({
  children,
  minimo,
}: {
  children: React.ReactNode;
  minimo?: Papel;
}) {
  const { isSignedIn, isLoading } = useAuth();
  const { papel, carregando } = usePapel();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Atalho de teste — SÓ em build de desenvolvimento.
  //
  // `?bypass=test` abria QUALQUER rota protegida sem sessão, inclusive
  // /admin/turmas (provisiona contas de aluno) e o console da aula. Era um
  // atalho de dev que foi junto pro bundle de produção. `import.meta.env.DEV`
  // é resolvido em build, então em produção este ramo some do código.
  if (import.meta.env.DEV && searchParams.get('bypass') === 'test') {
    return <>{children}</>;
  }

  // Enquanto o papel não é conhecido, NÃO renderizar o conteúdo — mostrar a
  // tela "enquanto confere" seria o mesmo vazamento que a tranca fecha.
  if (isLoading || (minimo && carregando)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isSignedIn) {
    // Guarda o destino: sem isso o professor que abre /console cai no login e,
    // depois de entrar, é jogado na home — tendo que digitar /console de novo.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (minimo && !alcanca(papel, minimo)) {
    return <SemPermissao />;
  }

  return <>{children}</>;
}

/**
 * Porta fechada com explicação. Uma tela vazia faria o aluno achar que quebrou
 * e chamar o professor no meio da aula; e um redirect silencioso faria o
 * professor legítimo achar que o link está errado.
 */
function SemPermissao() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <ShieldAlert className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
      <h1 className="text-xl font-bold">Esta área não é sua</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        Sua conta não tem permissão para abrir esta tela. Se você deveria ter,
        peça para a coordenação da escola liberar.
      </p>
      <Button variant="outline" asChild>
        <Link to="/">Voltar para o começo</Link>
      </Button>
    </div>
  );
}

/**
 * A ÁREA DO ALUNO NÃO FAZ PARTE DO NEXTGEN.
 *
 * `/aluno` é o catálogo do produto ASSÍNCRONO do 12brain — em inglês, com i18n
 * que nenhum componente consome. Decisão de 03/08: no NEXTGEN o aluno entra
 * pela sala, a plataforma é do professor.
 *
 * Redireciona em vez de bloquear: "Esta área não é sua" é sobre PAPEL, e aqui
 * não é o papel que está errado — é o endereço. Quem cai aqui vai pra casa do
 * próprio papel, então professor volta pro console e aluno vai pra sala. Beco
 * sem saída não é bloqueio, é armadilha.
 */
function ForaDesteProduto({ children }: { children: React.ReactNode }) {
  const { papel, carregando } = usePapel();
  const alunoPelaSala = ALUNO_ENTRA_PELA_SALA.has(FIXED_BRAND_ID ?? '');

  if (!alunoPelaSala) return <>{children}</>;
  if (carregando) return <PageLoader />;
  return <Navigate to={homeDoPapel(papel, true, TEM_MENU_LATERAL)} replace />;
}

/**
 * A raiz manda cada um pra sua casa.
 *
 * Antes ia pro `HOME_ROUTE` da MARCA, que na Globall é `/console` — a mesa do
 * professor. Um aluno que caísse na raiz (é o que acontece quando um link de
 * recuperação perde o `redirect_to`) batia na tela de quem dá aula.
 *
 * Agora quem decide é o PAPEL, não o deploy: o mesmo banco serve os dois hosts,
 * então a professora que entra pelo academy é a mesma que entra pelo nextgen.
 */
function RaizPorPapel() {
  const { isSignedIn, isLoading } = useAuth();
  const { papel, carregando } = usePapel();

  if (isLoading || carregando) return <PageLoader />;
  if (!isSignedIn) return <Navigate to="/login" replace />;
  return <Navigate to={homeDoPapel(papel, ALUNO_ENTRA_PELA_SALA.has(FIXED_BRAND_ID ?? ''), TEM_MENU_LATERAL)} replace />;
}

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

/**
 * Uma tela do produto: porta de papel + casca com menu.
 *
 * A ordem importa. `ProtectedRoute` fica POR FORA: quem não passa é redirigido
 * pro login ou recebe "Esta área não é sua" — e nenhum dos dois deve vir com o
 * menu montado em volta, que seria mostrar os destinos de uma casa que não é a
 * dele. A `Casca` é no-op em marca sem menu lateral, então o 12brain Academy
 * atravessa isto sem mudar um pixel.
 */
function Tela({ minimo, children }: { minimo?: Papel; children: React.ReactNode }) {
  return (
    <ProtectedRoute minimo={minimo}>
      {/* A PORTA DA MATRÍCULA fica DENTRO do gate de papel e FORA da casca: o
          aluno pendente não vê menu nenhum atrás do modal — menu com destinos
          que ele não pode usar é a porta fechada de novo, só que em volta. */}
      <PortaDaMatricula>
        <Casca>{children}</Casca>
      </PortaDaMatricula>
    </ProtectedRoute>
  );
}

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
  <Routes>
    {/* Quem decide pra onde vai é o PAPEL, não a marca — aluno na raiz ia bater
        no /console, a mesa do professor. */}
    <Route path="/" element={<RaizPorPapel />} />
    <Route
      path="/login"
      element={<AuthPage />}
    />
    {/* O aluno cria a conta com o CÓDIGO da turma (13/08). Pública como o
        login: quem chega aqui, por definição, ainda não tem conta. */}
    <Route path="/criar-conta" element={<CriarConta />} />
    {/* CONTEÚDO É DA 12BRAIN. Curso, aula, missão e o que está liberado são
        decisão de quem produz, não da escola — nem o diretor entra aqui. */}
    <Route
      path="/admin"
      element={
        <Tela minimo="superadmin">
          <Admin />
        </Tela>
      }
    />
    <Route
      path="/admin/analytics"
      element={
        <Tela minimo="superadmin">
          <Analytics />
        </Tela>
      }
    />
    <Route
      path="/admin/logs"
      element={
        <Tela minimo="superadmin">
          <Logs />
        </Tela>
      }
    />
    <Route
      path="/admin/edit-video/:id"
      element={
        <Tela minimo="superadmin">
          <EditVideo />
        </Tela>
      }
    />
    {/* MODO CLASSE — turmas e provisionamento de contas de aluno.
        Coordenador pra cima (decisão de 06/08): montar turma, matricular e
        escolher curso é gestão da escola, não do professor — ele rege a aula
        pelo /console e mantém só o SOCORRO em sala (nova senha, liberar
        aparelho), que mora no console da sessão. A migration 20260807000004 e
        o class-roster reconferem no servidor; a tranca aqui é a porta. */}
    <Route
      path="/admin/turmas"
      element={
        <Tela minimo="professor">
          <AdminTurmas />
        </Tela>
      }
    />
    {/* Os alunos da escola numa lista só. Mesmo gate das turmas — quem lê o
        roster inteiro é a coordenação (is_class_teacher cobre a escola dela).
        Provisionar em LOTE não mora aqui: a folha de senhas é por turma. */}
    <Route
      path="/admin/alunos"
      element={
        <Tela minimo="professor">
          <AdminAlunos />
        </Tela>
      }
    />
    {/* MODO CLASSE — quem trabalha na escola. Coordenador pra cima: professor
        não cadastra professor (a matriz da spec), e a Edge Function
        `staff-roster` reconfere isso no servidor. */}
    <Route
      path="/admin/escola"
      element={
        <Tela minimo="coordenador">
          <AdminEscola />
        </Tela>
      }
    />
    {/* MODO CLASSE — celular do aluno. Rota PÚBLICA de propósito: quem escaneia
        o QR chega deslogado e entra com o próprio e-mail e senha; a Edge
        Function class-room só confere que ele é daquela turma. */}
    <Route path="/sala/:codigo" element={<Sala />} />
    {/* Onde o link de recuperação de senha aterrissa. Precisa ser pública: o
        aluno chega aqui justamente porque NÃO consegue entrar. */}
    <Route path="/nova-senha" element={<NovaSenha />} />
    {/* Digitar o código da sala. PÚBLICA como a /sala: o aluno chega deslogado,
        e é o plano B de quando o QR não lê — antes não havia plano B. */}
    <Route path="/entrar" element={<Casca><EntrarNaSala /></Casca>} />
    {/* Trocar a PRÓPRIA senha. Qualquer papel — inclusive aluno, que é quem
        mais precisa: ele entra com a provisória da folha e quer uma que
        lembre. Protegida só por sessão: a tela pede a senha atual. */}
    <Route
      path="/trocar-senha"
      element={
        <Tela>
          <TrocarSenha />
        </Tela>
      }
    />
    {/* A própria conta: nome, e-mail, papel, escola. Qualquer papel — a RLS de
        `profiles` limita a leitura e a edição à própria linha. */}
    <Route
      path="/perfil"
      element={
        <Tela>
          <Perfil />
        </Tela>
      }
    />
    {/* Painel agregado da escola: onde cada turma parou. Coordenador pra
        cima — professor acompanha a PRÓPRIA turma pelo console e registro. */}
    <Route
      path="/escola"
      element={
        <Tela minimo="coordenador">
          <PainelEscola />
        </Tela>
      }
    />
    {/* A CASA do aluno do NEXTGEN (decisão de 07/08): histórico com
        reassistir, missões, quiz — e o /entrar virou botão dentro dela. */}
    <Route
      path="/minhas-aulas"
      element={
        <Tela>
          <MinhasAulas />
        </Tela>
      }
    />
    {/* A aba Recursos da casa do aluno tem rota própria (19/08): assim o item
        do MENU LATERAL acende certo e a aba sobrevive a recarregar a página.
        É o MESMO componente da casa — a rota só diz qual aba abre. */}
    <Route
      path="/recursos"
      element={
        <Tela>
          <MinhasAulas />
        </Tela>
      }
    />
    <Route
      path="/minhas-aulas/:videoId"
      element={
        <Tela>
          <ReassistirAula />
        </Tela>
      }
    />
    {/* A CASA de quem trabalha na escola (12/08). Vem antes do /console porque
        é pra cá que o login manda: o console abre com um select vazio, e chegar
        numa pergunta não é chegar no trabalho. */}
    <Route
      path="/painel"
      element={
        <Tela minimo="professor">
          <Painel />
        </Tela>
      }
    />
    {/* MODO CLASSE — console do professor. A RLS de class_sessions garante que
        só o dono da sessão consegue mudar o estado; a tela é o controle remoto. */}
    <Route
      path="/console"
      element={
        <Tela minimo="professor">
          <ClassConsole />
        </Tela>
      }
    />
    <Route
      path="/console/:sessionId"
      element={
        <Tela minimo="professor">
          <ClassSessionConsole />
        </Tela>
      }
    />
    {/* MODO CLASSE — o que a turma respondeu. Leitura autorizada pela policy
        "Teachers can view submissions from their class sessions" (PR1). */}
    {/* Registro da turma inteira — o histórico que atravessa as aulas.
        Vem ANTES de /console/:sessionId senão "registro" casaria como id. */}
    <Route
      path="/console/registro/:classId"
      element={
        <Tela minimo="professor">
          <ClassRegistro />
        </Tela>
      }
    />
    <Route
      path="/console/:sessionId/resultados"
      element={
        <Tela minimo="professor">
          <ClassResultados />
        </Tela>
      }
    />
    {/* MODO CLASSE — telão projetado. Protegida de propósito: é o telão que tem
        o relógio do vídeo e dispara a auto-pausa, e só o professor pode mudar
        o estado da sessão (RLS). Quem abre isso é o projetor da sala. */}
    <Route
      path="/telao/:sessionId"
      element={
        <ProtectedRoute minimo="professor">
          <Telao />
        </ProtectedRoute>
      }
    />
    <Route
      path="/aluno"
      element={
        <ProtectedRoute>
          <ForaDesteProduto>
            <CourseCatalog />
          </ForaDesteProduto>
        </ProtectedRoute>
      }
    />
    <Route
      path="/aluno/curso/:moduleId"
      element={
        <ProtectedRoute>
          <ForaDesteProduto>
            <CourseDetail />
          </ForaDesteProduto>
        </ProtectedRoute>
      }
    />
    <Route
      path="/aluno/aula/:videoId"
      element={
        <ProtectedRoute>
          <ForaDesteProduto>
            <Student />
          </ForaDesteProduto>
        </ProtectedRoute>
      }
    />
    <Route
      path="/aluno/dashboard"
      element={
        <ProtectedRoute>
          <ForaDesteProduto>
            <StudentDashboard />
          </ForaDesteProduto>
        </ProtectedRoute>
      }
    />
    <Route path="/verificar/:code" element={<VerifyCertificate />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
  </Suspense>
);

const AppContent = () => {
  const [locale, setLocale] = useState<SupportedLocale>(getSavedLocale());
  const messages = LOCALES[locale].messages;

  const handleLocaleChange = (newLocale: SupportedLocale) => {
    setLocale(newLocale);
    saveLocale(newLocale);
  };

  return (
    <IntlProvider locale={locale} messages={messages} defaultLocale="en-US">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </BrowserRouter>
            {/* Floating error/log debug button — dev only, never shown to students. */}
            {import.meta.env.DEV && <DebugPanel />}
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </IntlProvider>
  );
};

const App = () => (
  <BrandingProvider>
    <AppContent />
  </BrandingProvider>
);

export default App;
